import BackendAuthHandler from './BackendAuthHandler';
import { AdminChatAssistResult } from '../../models/admin/AdminChatAssistResult';
import type { ChatResolveDraftResult } from '../../models/admin/AdminChatResolveDraft';
import type {
  ChatLastParticipantAddedActivity,
  ChatParticipantAddedEvent,
} from '../../utils/chatParticipantAddedNote';

export interface AdminConversation {
  conversationId: string;
  status: 'open' | 'resolved';
  context: {
    screen: string;
    fundOpportunityExternalId?: string | null;
    fundUpdateId?: string | null;
  } | null;
  unreadByAdmin: boolean;
  lastMessageAt: string | null;
  lastMessageText: string | null;
  lastActivity?: ChatLastParticipantAddedActivity | null;
  ownerAdminExternalId: string | null;
  ownerAdminName: string | null;
  contact: {
    externalId: string;
    name: string;
    email: string;
  } | null;
}

export interface ChatMessageContext {
  screen: string;
  platform?: string | null;
  fundOpportunityExternalId?: string | null;
  fundOpportunityName?: string | null;
  investorExternalId?: string | null;
  fundUpdateId?: string | null;
}

export interface ChatMessage {
  id: string;
  senderId: string;
  senderName: string;
  senderRole: 'investor' | 'admin';
  text: string;
  timestamp: any;
  /** Screen context snapshot at send time — only present on investor messages. */
  context?: ChatMessageContext | null;
}

export interface ChatParticipant {
  adminExternalId: string;
  adminName: string | null;
  isOwner: boolean;
  addedAt: string;
}

export interface ChatParticipantsResponse {
  ownerAdminUserId: number | null;
  participants: ChatParticipant[];
}

export interface ChatTranscriptResponse {
  conversationId: string;
  contactExternalId: string;
  contactName: string | null;
  status: 'open' | 'resolved';
  context: ChatMessageContext | null;
  resolvedBy: 'admin' | 'investor' | null;
  messages: ChatMessage[];
  events: ChatParticipantAddedEvent[];
}

interface DataResponse<T> {
  data: T;
}

export class FidesChatAdminService {
  private async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const token = await new BackendAuthHandler().withAuthToken();

    if (!token) {
      throw new Error('Admin authentication required');
    }

    const response = await fetch(path, {
      credentials: 'include',
      ...init,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...(init.headers ?? {}),
      },
    });

    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || `Request failed with status ${response.status}`);
    }

    if (response.status === 204) {
      return undefined as T;
    }

    return (await response.json()) as T;
  }

  async listConversations(status?: 'open' | 'resolved'): Promise<AdminConversation[]> {
    const query = status ? `?status=${status}` : '';
    const response = await this.request<DataResponse<AdminConversation[]>>(
      `/api/chat/conversations${query}`,
      { method: 'GET' },
    );

    return response.data;
  }

  async getConversationTimeline(conversationId: string): Promise<{
    messages: ChatMessage[];
    events: ChatParticipantAddedEvent[];
  }> {
    const response = await this.request<
      DataResponse<{
        messages: ChatMessage[];
        events: ChatParticipantAddedEvent[];
      }>
    >(`/api/chat/conversations/${conversationId}/messages`, { method: 'GET' });
    return response.data;
  }

  async sendMessage(conversationId: string, text: string): Promise<void> {
    await this.request(`/api/chat/conversations/${conversationId}/messages`, {
      method: 'POST',
      body: JSON.stringify({ text }),
    });
  }

  async markRead(conversationId: string): Promise<void> {
    await this.request(`/api/chat/conversations/${conversationId}/read`, {
      method: 'PATCH',
    });
  }

  async reportViewing(conversationId: string): Promise<void> {
    await this.request(`/api/chat/conversations/${conversationId}/viewing`, {
      method: 'PATCH',
    });
  }

  async clearViewing(conversationId: string): Promise<void> {
    await this.request(`/api/chat/conversations/${conversationId}/viewing`, {
      method: 'DELETE',
    });
  }

  async getTranscript(conversationId: string): Promise<ChatTranscriptResponse> {
    const response = await this.request<DataResponse<ChatTranscriptResponse>>(
      `/api/chat/conversations/${conversationId}/transcript`,
      { method: 'GET' },
    );
    return response.data;
  }

  async listParticipants(conversationId: string): Promise<ChatParticipantsResponse> {
    const response = await this.request<DataResponse<ChatParticipantsResponse>>(
      `/api/chat/conversations/${conversationId}/participants`,
      { method: 'GET' },
    );
    return response.data;
  }

  async claimConversation(conversationId: string): Promise<void> {
    await this.request(`/api/chat/conversations/${conversationId}/claim`, {
      method: 'POST',
    });
  }

  async addParticipant(conversationId: string, adminExternalId: string): Promise<void> {
    await this.request(`/api/chat/conversations/${conversationId}/participants`, {
      method: 'POST',
      body: JSON.stringify({ adminExternalId }),
    });
  }

  async removeParticipant(conversationId: string, adminExternalId: string): Promise<void> {
    await this.request(
      `/api/chat/conversations/${conversationId}/participants/${adminExternalId}`,
      { method: 'DELETE' },
    );
  }

  async transferOwnership(conversationId: string, adminExternalId: string): Promise<void> {
    await this.request(`/api/chat/conversations/${conversationId}/owner`, {
      method: 'PATCH',
      body: JSON.stringify({ adminExternalId }),
    });
  }

  async requestAiAssist(
    conversationId: string,
    options?: { force?: boolean; targetMessageId?: string },
  ): Promise<AdminChatAssistResult> {
    const response = await this.request<DataResponse<AdminChatAssistResult>>(
      `/api/chat/conversations/${conversationId}/ai-assist`,
      {
        method: 'POST',
        body: JSON.stringify({
          force: options?.force === true,
          targetMessageId: options?.targetMessageId,
        }),
      },
    );
    return response.data;
  }

  async listAiAssistHistory(conversationId: string): Promise<AdminChatAssistResult[]> {
    const response = await this.request<DataResponse<AdminChatAssistResult[]>>(
      `/api/chat/conversations/${conversationId}/ai-assist/history`,
      { method: 'GET' },
    );
    return response.data ?? [];
  }

  async requestAiAssistFollowUp(
    conversationId: string,
    payload: { targetMessageId: string; question: string; followUpId?: string },
  ): Promise<AdminChatAssistResult> {
    const response = await this.request<DataResponse<AdminChatAssistResult>>(
      `/api/chat/conversations/${conversationId}/ai-assist/follow-up`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
    );
    return response.data;
  }

  async generateResolveDrafts(conversationId: string): Promise<ChatResolveDraftResult> {
    const response = await this.request<DataResponse<ChatResolveDraftResult>>(
      `/api/chat/conversations/${conversationId}/resolve-drafts`,
      {
        method: 'POST',
        body: JSON.stringify({ clientDate: new Date().toISOString() }),
      },
    );
    return response.data;
  }

  async commitResolveDrafts(
    conversationId: string,
    payload: {
      notes: Array<{
        content: string;
        modelType: string;
        modelExternalId: string;
        dueAt?: string | null;
      }>;
      actions: Array<{
        title: string;
        description?: string | null;
        modelType?: string | null;
        modelExternalId?: string | null;
        dueAt?: string | null;
      }>;
    },
  ): Promise<{ noteExternalIds: string[]; actionExternalIds: string[] }> {
    const response = await this.request<
      DataResponse<{ noteExternalIds: string[]; actionExternalIds: string[] }>
    >(`/api/chat/conversations/${conversationId}/resolve-drafts/commit`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return response.data;
  }
}

export default new FidesChatAdminService();
